"use client";

/**
 * Auth 훅 모음
 *
 * AuthContext에서 auth 상태와 액션을 소비한다.
 */
import { useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";

import type { ApiClient } from "@/shared/api/client";
import { useAuthContext } from "@/shared/auth/provider";

/**
 * 인증 상태 + 액션 훅
 *
 * @returns user, isAuthenticated, isLoading, login, logout, apiClient
 */
export function useAuth() {
  return useAuthContext();
}

/**
 * Buyer apiClient 로 도메인 service 인스턴스를 메모이즈해 반환.
 *
 * 27곳에서 반복되던 `useMemo(() => createXxxService(apiClient), [apiClient])`
 * 패턴을 한 줄로 줄여준다.
 *
 * @example
 *   const service = useBuyerService(createProductService);
 */
export function useBuyerService<S>(factory: (client: ApiClient) => S): S {
  const { apiClient } = useAuthContext();
  return useMemo(() => factory(apiClient), [factory, apiClient]);
}

/**
 * 인증 필수 페이지 훅
 *
 * 인증되지 않은 경우 로그인 페이지로 리다이렉트한다.
 * returnUrl을 query param으로 보존한다.
 *
 * @param redirectTo - 로그인 후 돌아올 경로 (기본: 현재 경로)
 */
export function useRequireAuth(redirectTo?: string) {
  const { isAuthenticated, isLoading } = useAuthContext();
  const router = useRouter();

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      if (window.location.pathname === "/login") return;
      const returnUrl = redirectTo ?? window.location.pathname + window.location.search;
      router.push("/login?returnUrl=" + encodeURIComponent(returnUrl));
    }
  }, [isAuthenticated, isLoading, redirectTo, router]);

  return { isAuthenticated, isLoading };
}
